import { Map, List } from 'immutable'
import React, {
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import { connect as connectRedux } from 'react-redux'
import { getBoundActions } from '../../actions/index'
import { getPrimary } from '../../services/languages/index'
import { getAuthIsLoggedIn } from '../../services/auth/index'
import { UNAVAILABLE_REASON } from '../../services/share/index'
import { historyRedirect, HISTORY_METHOD_REPLACE } from '../../services/navigation/index'
import { PLAN_SKU_THREE_MONTH_TO_MONTHLY_SPECIAL } from '../../services/plans/index'
import { URL_GO_PAGE } from '../../services/url/constants'
import { TYPE_CONTENT_VIDEO } from '../../services/content-type/index'
import { SHARE_OFFER_CLICKED_GA4_EVENT } from '../../services/event-tracking/index'
import { useGetPlanDataBySku } from '../../services/plans/hooks'
import { isEntitled } from '../../services/subscription/index'
import { createUpstreamContext, upstreamContextOnClick } from '../../services/upstream-context/index'
import PlanCardGrid from '../PlanCardGrid/index'
import { EN } from '../../services/languages/constants'
import { H2 } from '../Heading/index'
import { SCREEN_TYPE_SHARE_WATCH } from '../../services/page-manager/entities/screenType'
import SharePageHeader from './SharePageHeader'
import SharePageMeta from './SharePageMeta'
import SharePageVideo from './SharePageVideo'
import SharePageSpecialOffer, {
  OFFER_DISPLAY_TYPE_PROMO,
  OFFER_DISPLAY_TYPE_CARD,
} from './SharePageSpecialOffer'
import SharePageBanner from './SharePageBanner'
import SharePageVideoLimitView from './SharePageVideoLimitView'
import SharePageMediaShowcase, { BACKGROUND_STYLES } from './SharePageMediaShowcase'
import SharePageInfoBlock from './SharePageInfoBlock'
import SharePageHeroLogos from './SharePageHeroLogos'
import SharePagePerks from './SharePagePerks'
import SharePageSubValue from './SharePageSubValue'
import SharePageTestimonials from './SharePageTestimonials'
import SharePageFaqAccordion from './SharePageFaqAccordion'

const OFFER_LINK = {
  url: URL_GO_PAGE,
  query: {
    sku: PLAN_SKU_THREE_MONTH_TO_MONTHLY_SPECIAL,
  },
}

// UI element identifiers for tracking
const UI_ELEMENTS = {
  TOP_OFFER: 'top offer',
  HERE_FOR_REASON: 'here for a reason',
  AWARD_WINNING: 'award winning',
  DEVICES: 'devices',
  PLAN_SELECTED: 'plan selected',
  BOTTOM_OFFER: 'bottom offer',
}

// Media showcase section keys
const SECTION_KEYS = {
  MEMBER_FAVORITES: 'member-favorites',
  ORIGINAL_SERIES: 'original-series',
  AWARD_WINNING: 'award-winning',
  YOGA_MEDITATION: 'yoga-meditation',
}

// Helper function to index sections by adminTitle
function indexByAdminTitle (pmSection) {
  return pmSection.reduce((acc, section) => {
    const title = section.getIn(['data', 'adminTitle'])
    return acc.set(title, section.get('data'))
  }, Map())
}

function SharePage ({
  sendOptimizelyFeatureEvent,
  setEventDefault,
  setUpstreamContext,
  setQualifiedView,
  getPmScreen,
  staticText,
  screenType,
  pmSection,
  history,
  error,
  share,
  plans,
  auth,
  user,
  userAccount,
}) {
  // User and authentication data
  const language = getPrimary(user.getIn(['data', 'language'], List()))
  const authJwt = auth.get('jwt')
  const isLoggedIn = getAuthIsLoggedIn(auth)
  const isUserEntitled = isEntitled(auth.get('subscriptions', List()))
  const billingAccountId = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'billingAccountId'])
  const showRenew = isLoggedIn && !isUserEntitled && billingAccountId

  // Get plan data using GraphQL
  const gqlPlanData = useGetPlanDataBySku({
    userLanguage: language,
    authJwt,
    planSkus: PLAN_SKU_THREE_MONTH_TO_MONTHLY_SPECIAL,
  }) || Map()
  const formattedPlanData = gqlPlanData.get('plans', List()) || List()

  // Calculate offerPlan using useMemo for optimization
  const offerPlan = useMemo(() => {
    if (List.isList(formattedPlanData) && formattedPlanData.size) {
      return formattedPlanData.get(0)
    }
    if (plans && plans.size > 0) {
      return plans.find((p) => p.get('sku') === PLAN_SKU_THREE_MONTH_TO_MONTHLY_SPECIAL) || Map()
    }
    return Map()
  }, [formattedPlanData, plans])

  // Share data
  const shareId = share.get('id')
  const userReferralId = share.get('userReferralId')
  const content = share.get('content', Map())
  const unavailableReason = share.get('unavailableReason')
  const expirationDate = share.get('expirationDate')
  const expired = share.get('expired')
  const contentUrl = content.get('url')

  // Process media showcase sections using useMemo
  const sectionsData = useMemo(() => indexByAdminTitle(pmSection), [pmSection])
  const memberFavoritesData = sectionsData.getIn([SECTION_KEYS.MEMBER_FAVORITES, 'data'])
  const originalSeriesData = sectionsData.getIn([SECTION_KEYS.ORIGINAL_SERIES, 'data'])
  const awardWinningData = sectionsData.getIn([SECTION_KEYS.AWARD_WINNING, 'data'])
  const yogaData = sectionsData.getIn([SECTION_KEYS.YOGA_MEDITATION, 'data'])

  // Check if there's a specific unavailable reason
  const shareSpecificReason = useMemo(() => {
    const unavailableReasons = [
      UNAVAILABLE_REASON.EMAIL_VIEW_LIMIT_EXCEEDED,
      UNAVAILABLE_REASON.VIEW_LIMIT_EXCEEDED,
      UNAVAILABLE_REASON.TIME_LIMIT_EXCEEDED,
    ]
    return unavailableReason && unavailableReasons.includes(unavailableReason)
  }, [unavailableReason])

  // Handler for offer clicks using useCallback
  const handleOfferClick = useCallback((e, uiElement) => {
    const contentData = share.get('content', Map())
    const seriesId = contentData.get('seriesId')

    const eventData = {
      content_id: contentData.get('contentId'),
      content_name: contentData.get('title'),
      content_type: TYPE_CONTENT_VIDEO,
      ui_element: uiElement,
      ui_location: 'Share Watch',
      ...(seriesId && {
        parent_series_id: seriesId,
        parent_series_name: contentData.get('seriesTitle'),
      }),
    }

    setEventDefault(SHARE_OFFER_CLICKED_GA4_EVENT.mergeDeep(eventData))
    sendOptimizelyFeatureEvent({
      eventName: 'share_offer_clicked',
      element: uiElement,
    })

    // Update upstream context
    const updatedContext = createUpstreamContext({ sharePageAttribution: { click_attribution: 'Share' } })
    upstreamContextOnClick(e, { upstreamContext: updatedContext, setUpstreamContext })
  }, [share, setEventDefault])

  useEffect(() => {
    if (isLoggedIn && isUserEntitled) {
      // Entitled user is always redirected to the content page
      historyRedirect({
        url: contentUrl || '/not-found',
        language,
        history,
        auth,
        historyMethod: HISTORY_METHOD_REPLACE,
      })
    }
  }, [isLoggedIn, isUserEntitled, contentUrl])

  useEffect(() => {
    if (!isUserEntitled && !shareSpecificReason) {
      getPmScreen({ screenType, language })
    }
  }, [isUserEntitled, shareSpecificReason, language])

  // Early return for specific unavailable reason
  if (shareSpecificReason) {
    return (
      <section className="share-page">
        <SharePageVideoLimitView
          staticText={staticText}
          isLoggedIn={isLoggedIn}
          unavailableReason={unavailableReason}
          showRenew={showRenew}
        />
      </section>
    )
  }

  return (
    <section className="share-page">
      <SharePageHeader shareId={shareId} userReferralId={userReferralId} />
      <div className="share-page__top-content">
        <SharePageVideo
          isAnonymous={!isLoggedIn}
          share={share}
          error={error}
          staticText={staticText}
          language={language}
          setQualifiedView={setQualifiedView}
        />
        {!error && !expired && (
          <div className="share-page__top-content-offer">
            <SharePageSpecialOffer
              staticText={staticText}
              offerPlan={offerPlan}
              expirationDate={expirationDate}
              offerLink={OFFER_LINK}
              onOfferClick={(e) => handleOfferClick(e, UI_ELEMENTS.TOP_OFFER)}
              language={language}
            />
          </div>
        )}
      </div>
      {!error && content && (
        <SharePageMeta staticText={staticText} content={content} />
      )}
      <SharePagePerks staticText={staticText} />
      {memberFavoritesData && (
        <SharePageMediaShowcase
          sectionData={memberFavoritesData}
          language={language}
          autoplay={true}
        />
      )}
      <SharePageBanner
        title={staticText.get('reasonTitle')}
        text={staticText.get('reasonText2')}
        subtitle={staticText.get('reasonSubTitle')}
        ctaLabel={staticText.get('getStarted')}
        offerLink={OFFER_LINK}
        onOfferClick={(e) => handleOfferClick(e, UI_ELEMENTS.HERE_FOR_REASON)}
      />
      {originalSeriesData && (
        <SharePageMediaShowcase
          sectionData={originalSeriesData}
          language={language}
          pmSection={pmSection}
          backgroundStyle={BACKGROUND_STYLES.CURVED_GRADIENT}
        />
      )}
      {awardWinningData && (
        <SharePageMediaShowcase
          sectionData={awardWinningData}
          language={language}
          pmSection={pmSection}
          backgroundStyle={BACKGROUND_STYLES.CURVED_WHITE}
          ctaLabel={staticText.get('getStarted')}
          offerLink={OFFER_LINK}
          onOfferClick={(e) => handleOfferClick(e, UI_ELEMENTS.AWARD_WINNING)}
        />
      )}
      {yogaData && (
        <SharePageMediaShowcase
          sectionData={yogaData}
          language={language}
          pmSection={pmSection}
          backgroundStyle={BACKGROUND_STYLES.CURVED_GRADIENT}
        />
      )}
      <SharePageInfoBlock staticText={staticText} />
      <SharePageHeroLogos
        staticText={staticText}
        ctaLabel={staticText.get('getStarted')}
        offerLink={OFFER_LINK}
        onOfferClick={(e) => handleOfferClick(e, UI_ELEMENTS.DEVICES)}
      />
      {language !== EN && (
        <div className="share-page__plans">
          <div className="share-page__subtitle share-page__plans-subtitle">{staticText.get('plansSubtitle')}</div>
          <H2 className="share-page__title share-page__plans-title">{staticText.get('plansTitle')}</H2>
          <PlanCardGrid
            history={history}
            shouldScrollToTop
            onPlanSelect={(e) => handleOfferClick(e, UI_ELEMENTS.PLAN_SELECTED)}
          />
        </div>
      )}
      <SharePageSubValue staticText={staticText} />
      <SharePageTestimonials staticText={staticText} />
      <SharePageFaqAccordion staticText={staticText} offerPlan={offerPlan} language={language} />
      {!error && !expired && (
        <div className="share-page__bottom-content">
          <div className="share-page__bottom-content-offer--promo">
            <SharePageSpecialOffer
              staticText={staticText}
              offerPlan={offerPlan}
              expirationDate={expirationDate}
              offerLink={OFFER_LINK}
              displayType={OFFER_DISPLAY_TYPE_PROMO}
              onOfferClick={(e) => handleOfferClick(e, UI_ELEMENTS.BOTTOM_OFFER)}
              language={language}
            />
          </div>
          <div className="share-page__bottom-content-offer--card">
            <SharePageSpecialOffer
              staticText={staticText}
              offerPlan={offerPlan}
              expirationDate={expirationDate}
              offerLink={OFFER_LINK}
              displayType={OFFER_DISPLAY_TYPE_CARD}
              onOfferClick={(e) => handleOfferClick(e, UI_ELEMENTS.BOTTOM_OFFER)}
              language={language}
            />
          </div>
        </div>
      )}
    </section>
  )
}

export default connectRedux(({
  staticText,
  pmSection,
  share,
  plans,
  user,
  auth,
  userAccount,
}) => ({
  staticText: staticText.getIn(['data', 'sharePage', 'data'], Map()),
  pmSection,
  share: share.get('data', Map()),
  error: share.get('success') === false,
  plans: plans.getIn(['data', 'plans']),
  auth,
  user,
  userAccount,
  screenType: SCREEN_TYPE_SHARE_WATCH,
}), (dispatch) => {
  const actions = getBoundActions(dispatch)
  return {
    sendOptimizelyFeatureEvent: actions.optimizelyFeature.sendOptimizelyFeatureEvent,
    setUpstreamContext: actions.upstreamContext.setUpstreamContext,
    setEventDefault: actions.eventTracking.setEventDefault,
    setQualifiedView: actions.share.setShareQualifiedView,
    getPmScreen: actions.pmScreen.getPmScreen,
  }
})(SharePage)
